import {
  EXTRA_LABEL_LAYER_IDS,
  OPENFREEMAP_ATTRIBUTION,
  OPENFREEMAP_DARK_STYLE_URL,
  extraLabelVisibility,
  patchBasemapStyle,
  satelliteBorderStyle,
} from "./basemap-style.js";

const BASEMAP_PANE = "basemapPane";
const BASEMAP_PANE_Z = "200";
// Just above the satellite (350), under the mosaic (360) and the radar.
const SAT_BORDERS_PANE = "satBordersPane";
const SAT_BORDERS_PANE_Z = "355";

/**
 * MapLibre's default world is 512px at zoom 0; Leaflet's is 256px.
 * The official adapter therefore uses MapLibreZoom = LeafletZoom - 1.
 * That offset is for scale matching, not visibility — a missing Map
 * constructor is what produced a blank page.
 */
export const LEAFLET_TO_MAPLIBRE_ZOOM_DELTA = -1;

let leafletLayer = null;
let maplibreMap = null;
let pendingExtraLabels = false;
let attachGeneration = 0;
let rawStylePromise = null;
let bordersLayer = null;
let bordersLoading = null;
let bordersWanted = false;

export function resolveMapLibreExport(mod) {
  if (!mod) return null;
  if (typeof mod.Map === "function") return mod;
  if (mod.default && typeof mod.default.Map === "function") return mod.default;
  return null;
}

export function leafletToMapLibreZoom(leafletZoom) {
  const z = Number(leafletZoom);
  if (!Number.isFinite(z)) return 0;
  return z + LEAFLET_TO_MAPLIBRE_ZOOM_DELTA;
}

// The basemap and the satellite borders share one download of the style.
function fetchRawStyle() {
  if (!rawStylePromise) {
    rawStylePromise = fetch(OPENFREEMAP_DARK_STYLE_URL).then((resp) => {
      if (!resp.ok) throw new Error(`OpenFreeMap style HTTP ${resp.status}`);
      return resp.json();
    });
    rawStylePromise.catch(() => { rawStylePromise = null; });
  }
  return rawStylePromise;
}

async function loadStyle(extraLabels) {
  return patchBasemapStyle(await fetchRawStyle(), { extraLabels });
}

function ensurePane(map, name, zIndex) {
  if (!map.getPane(name)) {
    const pane = map.createPane(name);
    pane.style.zIndex = zIndex;
    pane.style.pointerEvents = "none";
    pane.style.overflow = "visible";
  }
  return map.getPane(name);
}

function ensureBasemapPane(map) {
  return ensurePane(map, BASEMAP_PANE, BASEMAP_PANE_Z);
}

async function loadMapLibre() {
  await import("maplibre-gl/dist/maplibre-gl.css");
  return resolveMapLibreExport(await import("maplibre-gl"));
}

function applyExtraLabelVisibility(glMap, extraLabels) {
  if (!glMap || typeof glMap.getLayer !== "function") return;
  const visibility = extraLabelVisibility(extraLabels);
  for (const id of EXTRA_LABEL_LAYER_IDS) {
    if (glMap.getLayer(id)) {
      glMap.setLayoutProperty(id, "visibility", visibility);
    }
  }
}

function whenStyleReady(glMap, fn) {
  if (!glMap) return;
  if (typeof glMap.isStyleLoaded === "function" && glMap.isStyleLoaded()) {
    fn();
    return;
  }
  glMap.once("load", fn);
}

function createMapLibreLeafletLayer(L, maplibregl, style, paneName = BASEMAP_PANE, paneZ = BASEMAP_PANE_Z) {
  return L.Layer.extend({
    options: {
      pane: paneName,
      attribution: OPENFREEMAP_ATTRIBUTION,
    },

    onAdd: function onAdd(map) {
      this._map = map;
      const pane = ensurePane(map, paneName, paneZ);
      this._container = L.DomUtil.create("div", "leaflet-gl-layer maplibregl-map");
      this._container.style.position = "absolute";
      this._container.style.pointerEvents = "none";
      this._syncContainerSize();
      pane.appendChild(this._container);

      const center = map.getCenter();
      this._glMap = new maplibregl.Map({
        container: this._container,
        style,
        center: [center.lng, center.lat],
        zoom: leafletToMapLibreZoom(map.getZoom()),
        attributionControl: false,
        interactive: false,
        fadeDuration: 0,
        pitchWithRotate: false,
        dragRotate: false,
        renderWorldCopies: true,
        pixelRatio: Math.max(1, window.devicePixelRatio || 1),
      });

      this._onMove = this._syncCamera.bind(this);
      map.on("move zoom resize viewreset", this._onMove);
      this._glMap.on("load", this._onMove);
      this._syncCamera();
      return this;
    },

    onRemove: function onRemove(map) {
      if (this._onMove) {
        map.off("move zoom resize viewreset", this._onMove);
        if (this._glMap && typeof this._glMap.off === "function") {
          this._glMap.off("load", this._onMove);
        }
        this._onMove = null;
      }
      if (this._glMap) {
        try {
          this._glMap.remove();
        } catch {
          // Context loss must not take down Leaflet.
        }
        this._glMap = null;
      }
      if (this._container && this._container.parentNode) {
        this._container.parentNode.removeChild(this._container);
      }
      this._container = null;
      this._map = null;
      return this;
    },

    getMaplibreMap: function getMaplibreMap() {
      return this._glMap;
    },

    getAttribution: function getAttribution() {
      return OPENFREEMAP_ATTRIBUTION;
    },

    _syncContainerSize: function _syncContainerSize() {
      if (!this._map || !this._container) return;
      const size = this._map.getSize();
      const w = Math.max(1, size.x);
      const h = Math.max(1, size.y);
      this._container.style.width = `${w}px`;
      this._container.style.height = `${h}px`;
      const topLeft = this._map.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(this._container, topLeft);
    },

    _syncCamera: function _syncCamera() {
      if (!this._map || !this._glMap) return;
      this._syncContainerSize();
      const center = this._map.getCenter();
      this._glMap.jumpTo({
        center: [center.lng, center.lat],
        zoom: leafletToMapLibreZoom(this._map.getZoom()),
      });
      if (typeof this._glMap.resize === "function") {
        this._glMap.resize();
      }
    },
  });
}

/**
 * Embed MapLibre GL as a Leaflet layer under RadarGLLayer.
 * Failures are logged; the Leaflet map and radar overlay keep working.
 */
export async function attachVectorBasemap(map, options = {}) {
  pendingExtraLabels = !!options.extraLabels;
  const generation = ++attachGeneration;
  const L = options.L || globalThis.L;

  if (!map || typeof map.createPane !== "function" || !L?.Layer) {
    console.warn("MapLibre basemap skipped: Leaflet map is not ready.");
    return null;
  }

  ensureBasemapPane(map);

  let style = OPENFREEMAP_DARK_STYLE_URL;
  try {
    style = await loadStyle(pendingExtraLabels);
  } catch (err) {
    console.warn("OpenFreeMap style fetch failed; MapLibre will load the remote URL.", err);
    style = OPENFREEMAP_DARK_STYLE_URL;
  }

  if (generation !== attachGeneration) return null;

  let maplibregl;
  try {
    maplibregl = await loadMapLibre();
  } catch (err) {
    console.error("MapLibre failed to load; radar overlay still works.", err);
    return null;
  }

  if (!maplibregl) {
    console.error("maplibre-gl loaded but Map constructor was missing (UMD/ESM interop).");
    return null;
  }
  if (generation !== attachGeneration) return null;

  try {
    if (leafletLayer && map.hasLayer(leafletLayer)) {
      map.removeLayer(leafletLayer);
    }

    const LayerClass = createMapLibreLeafletLayer(L, maplibregl, style);
    leafletLayer = new LayerClass();
    leafletLayer.addTo(map);
    maplibreMap = leafletLayer.getMaplibreMap();

    if (maplibreMap) {
      whenStyleReady(maplibreMap, () => {
        if (generation !== attachGeneration) return;
        applyExtraLabelVisibility(maplibreMap, pendingExtraLabels);
      });
    }
  } catch (err) {
    console.error("MapLibre basemap failed to initialize; radar overlay still works.", err);
    leafletLayer = null;
    maplibreMap = null;
    return null;
  }

  return leafletLayer;
}

export function setBasemapExtraLabels(extraLabels) {
  pendingExtraLabels = !!extraLabels;
  if (!maplibreMap) return;
  whenStyleReady(maplibreMap, () => {
    applyExtraLabelVisibility(maplibreMap, pendingExtraLabels);
  });
}

export function resizeBasemap() {
  try {
    if (leafletLayer && typeof leafletLayer._syncCamera === "function") {
      leafletLayer._syncCamera();
      return;
    }
    if (maplibreMap && typeof maplibreMap.resize === "function") {
      maplibreMap.resize();
    }
  } catch {
    // A lost WebGL context here must not take down the Leaflet map.
  }
}

export function getBasemapLayer() {
  return leafletLayer;
}

async function createBordersLayer(map, L) {
  const [raw, maplibregl] = await Promise.all([fetchRawStyle(), loadMapLibre()]);
  if (!maplibregl) return null;
  const LayerClass = createMapLibreLeafletLayer(
    L,
    maplibregl,
    satelliteBorderStyle(raw),
    SAT_BORDERS_PANE,
    SAT_BORDERS_PANE_Z,
  );
  return new LayerClass();
}

/**
 * Faint state and country borders drawn above the GOES layer, whose imagery
 * hides the basemap's own. Built on first use; failures only lose the lines.
 */
export async function setSatelliteBorders(map, visible, options = {}) {
  bordersWanted = !!visible;
  const L = options.L || globalThis.L;
  if (!map || !L?.Layer) return;
  if (bordersWanted && !bordersLayer) {
    if (!bordersLoading) {
      bordersLoading = createBordersLayer(map, L)
        .catch((err) => {
          console.warn("Satellite borders unavailable.", err);
          return null;
        })
        .then((layer) => {
          bordersLayer = layer;
          bordersLoading = null;
          return layer;
        });
    }
    await bordersLoading;
  }
  if (!bordersLayer) return;
  try {
    // Faded out rather than removed: the satellite comes and goes with every
    // frame, and each add would build a new WebGL context. `display: none`
    // would drop the canvas, which then comes back blank until MapLibre next
    // redraws, so it is hidden with opacity and repainted when shown.
    if (bordersWanted && !map.hasLayer(bordersLayer)) bordersLayer.addTo(map);
    const pane = map.getPane(SAT_BORDERS_PANE);
    if (pane) pane.style.opacity = bordersWanted ? "1" : "0";
    if (bordersWanted) bordersLayer.getMaplibreMap()?.triggerRepaint?.();
  } catch (err) {
    console.warn("Satellite borders failed to draw.", err);
  }
}
